import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell
} from 'recharts';
import {
  Typography, Card, CardContent, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@material-ui/core';

const COLORS = ['#90caf9', '#64b5f6', '#42a5f5', '#1e88e5', '#1565c0'];

const CohortRetentionCard = ({ data, title = "Rétention des nouveaux inscrits" }) => {
  const cohortData = data?.cohort_retention || [];
  const totalInscrits = data?.total_nouveaux_inscrits || 0;

  // Préparer les données pour le graphique
  const chartData = cohortData.map((item, index) => ({
    ...item,
    name: item.label,
    value: item.retention_rate,
    color: COLORS[index % COLORS.length]
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          style={{
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            padding: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
            {data.label}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Taux de rétention: <strong>{data.retention_rate}%</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Utilisateurs qui ont commandé: <strong>{data.users_who_ordered}</strong> / {data.eligible_users} éligibles
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total commandes: <strong>{data.total_orders}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Moyenne/user: <strong>{data.avg_orders_per_user}</strong>
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ marginBottom: 16, display: 'block' }}>
          {totalInscrits} nouveaux inscrits sur la période - Suivi des commandes dans le temps
        </Typography>

        {/* Graphique en barres horizontales */}
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 40, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Tableau détaillé */}
        <Box mt={3}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Détails par fenêtre
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fenêtre</TableCell>
                  <TableCell align="right">Éligibles</TableCell>
                  <TableCell align="right">Utilisateurs qui ont commandé</TableCell>
                  <TableCell align="right">Commandes</TableCell>
                  <TableCell align="right">Taux</TableCell>
                  <TableCell align="right">Moy/user</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cohortData.map((row, index) => (
                  <TableRow key={row.label} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Box
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            backgroundColor: COLORS[index % COLORS.length],
                            marginRight: 8
                          }}
                        />
                        {row.label}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.eligible_users}</TableCell>
                    <TableCell align="right">{row.users_who_ordered}</TableCell>
                    <TableCell align="right">{row.total_orders}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          color: row.retention_rate >= 50 ? '#4caf50' :
                                 row.retention_rate >= 25 ? '#ff9800' : '#f44336'
                        }}
                      >
                        {row.retention_rate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.avg_orders_per_user}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CohortRetentionCard;
